import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyData from "go-core/components/EmptyData";
import { FormDirty } from "go-form/components/FormDirty";
import { adminApi } from "../../../../../../../../../services/Api/Admin/adminApi";
import {
	AddPartnerFormProps,
	PartnerApi,
	PartnerSearchSelectApi,
} from "../../../../../../../../../services/Api/Partner/partnerTypes";
import { OrganizationApi } from "../../../../../../../../../services/Api/types";
import AddOrUpdatePartnerProvisionModal from "../AddOrUpdatePartnerProvisionModal";
import AddPartnerTableForm from "./AddPartnerTableForm";

interface Props {
	organization: OrganizationApi;
}

const AddPartnerForm = ({ organization }: Props) => {
	const { t } = useTranslation("admin");
	const [loading, setLoading] = useState(false);
	const [updatedOrganization, setUpdatedOrganization] = useState(organization);
	const [showPartnerModal, setShowPartnerModal] = useState<number>(-1);
	const form = useForm<AddPartnerFormProps>({
		defaultValues: {
			partners: organization.partners.map((partner) => {
				return {
					partner_id: partner.partner_id,
					provisions: partner.provisions,
				};
			}),
		},
	});

	const { addFlash, addSuccessFlash } = useFlash();
	const { handleSubmit, getValues, setError, control, formState } = form;
	const { replace, remove, fields } = useFieldArray({
		keyName: "key",
		name: "partners",
		control,
	});

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			await adminApi.addPartnerToOrganization(organization.id, data);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	});

	const updateOrganizationPartners = (obj: PartnerSearchSelectApi) => {
		const newPartner = {
			partner_id: obj.id,
			name: obj.name,
			default_provisions: obj.default_provisions,
		} as unknown as PartnerApi;
		setUpdatedOrganization({ ...updatedOrganization, partners: [...updatedOrganization.partners, newPartner] });
	};

	const handleSave = (data: AddPartnerFormProps) => {
		replace(data.partners);
		setShowPartnerModal(-1);
	};

	const onHideModal = (data: AddPartnerFormProps) => {
		const updated = data.partners.filter((x) => x.partner_id);
		replace(updated);
		setShowPartnerModal(-1);
	};

	const onCancel = () => {
		setShowPartnerModal(-1);
	};

	return (
		<FormDirty formState={formState} onSubmit={onSubmit} loading={loading}>
			<fieldset className={"form-group"}>
				{!fields.length ? (
					<EmptyData
						title={t("modules.organization.field.empty_partner.title")}
						styles={{
							background: "#F5F8FA",
							marginBottom: "10px",
						}}
						description={t("modules.organization.field.empty_partner.description")}
						actions={[
							{
								name: `+${t("common.action.add", { ns: "lib" })}`,
								variant: "primary",
								click: () => setShowPartnerModal(0),
							},
						]}
					/>
				) : (
					<AddPartnerTableForm
						form={form}
						setShowPartnerModal={setShowPartnerModal}
						remove={remove}
						fields={fields}
						updatedOrganization={updatedOrganization}
					/>
				)}
				{fields.length > 0 && (
					<Button variant="add" type="button" onClick={() => setShowPartnerModal(fields.length)}>
						+ {t("modules.organization.action.assign_partner.title")}...
					</Button>
				)}
			</fieldset>
			{showPartnerModal >= 0 ? (
				<AddOrUpdatePartnerProvisionModal
					onCancel={onCancel}
					handleSave={handleSave}
					form={form}
					onHide={onHideModal}
					updateOrganizationPartners={updateOrganizationPartners}
					partner={updatedOrganization.partners.find(
						(f) => Number(f.partner_id) === Number(getValues(`partners.${showPartnerModal}.partner_id`))
					)}
					partnerIndex={showPartnerModal}
				/>
			) : null}
		</FormDirty>
	);
};

export default AddPartnerForm;
